import { flexRender } from '@tanstack/react-table'
import { useDndContext, useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { BodyTd } from '../../../../../components/stories/organisms/table/styled-components'
import { PlanMenuDraggableRow } from '../../../styled-component'
import { planMenuDragHandleColumnId } from '../table-options'
import SkeletonComponent from '../../../../../components/stories/atoms/skeleton'
import TableRowCell from '../../../../../components/stories/organisms/table/components/row/table-row-cell'
import {
  canDropItemsToFolder,
  canDropItemsToRoot,
  getActiveItem,
  getItemsToMove,
  menuTreeDropTargetTypeOptions,
  menuTreeItemTypeOptions
} from '../../../../../components/menu-tree/utils'

const stopDragActivation = (event) => event.stopPropagation()

const getCellDragProps = ({
  attributes,
  canDrag,
  cell,
  setActivatorNodeRef
}) => {
  // cells that own a control of their own (menus, checkbox) keep the activation
  // event from reaching the row, so using them never starts a drag
  if (cell.column.columnDef.meta?.preventDrag) {
    return { onMouseDown: stopDragActivation, onTouchStart: stopDragActivation }
  }

  if (!canDrag || cell.column.id !== planMenuDragHandleColumnId) return {}

  // `attributes` carries role/tabIndex, which would break the table semantics if
  // applied to the <tr>: the handle stays the keyboard entry point, and its
  // keydown bubbles up to the row listeners
  return { ...attributes, ref: setActivatorNodeRef }
}

const MovingCellSkeleton = ({ orientation }) => (
  <TableRowCell orientation={orientation}>
    <SkeletonComponent
      isLoading
      skeletonShape={{
        width: '100%',
        height: '16px',
        borderRadius: '6px'
      }}
    />
  </TableRowCell>
)

const PlanMenuTableRow = ({
  isMoving,
  isSelected,
  row,
  rowSelection,
  rowsById
}) => {
  const isFolder = row.original.type === menuTreeItemTypeOptions.FOLDER
  const isDraggable = !!row.original.isDraggable
  const canDrag = isDraggable && !isMoving
  const { active, over } = useDndContext()
  const activeItem = getActiveItem(active, rowsById)
  const itemsToMove = getItemsToMove({ activeItem, rowSelection, rowsById })
  const isRootLevel = row.depth === 0
  const canDropSelectionToRoot = canDropItemsToRoot(itemsToMove)
  const parentFolder =
    !isFolder && row.original.parentId
      ? rowsById.get(row.original.parentId)
      : null
  const canDropInFolder =
    isFolder && canDropItemsToFolder(itemsToMove, row.original)
  const canDropInParentFolder =
    !!parentFolder && canDropItemsToFolder(itemsToMove, parentFolder)
  const canDropToRoot = !isFolder && isRootLevel && canDropSelectionToRoot
  const isFolderGroupOver =
    isFolder &&
    over?.data.current?.type === menuTreeDropTargetTypeOptions.FOLDER &&
    over.data.current.folder?.id === row.original.id

  const {
    setNodeRef: setDraggableRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging
  } = useDraggable({
    id: row.id,
    data: { type: menuTreeDropTargetTypeOptions.ROW, item: row.original },
    disabled: !isDraggable
  })

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `folder-${row.original.id}`,
    data: {
      type: menuTreeDropTargetTypeOptions.FOLDER,
      folder: row.original
    },
    disabled: !isFolder
  })

  const { setNodeRef: setRowDroppableRef } = useDroppable({
    id: `row-${row.original.id}`,
    data: {
      type: menuTreeDropTargetTypeOptions.ROW,
      item: row.original
    },
    disabled: !isDraggable
  })

  const { setNodeRef: setRootDroppableRef } = useDroppable({
    id: `root-row-${row.original.id}`,
    data: {
      type: menuTreeDropTargetTypeOptions.ROOT_ROW
    },
    disabled: !canDropToRoot
  })

  const { setNodeRef: setParentFolderDroppableRef } = useDroppable({
    id: `folder-child-row-${row.original.id}`,
    data: {
      type: menuTreeDropTargetTypeOptions.FOLDER,
      folder: parentFolder
    },
    disabled: !canDropInParentFolder
  })

  const setNodeRef = (node) => {
    setDraggableRef(node)
    setRowDroppableRef(node)

    if (isFolder) {
      setDroppableRef(node)
    }

    if (canDropToRoot) {
      setRootDroppableRef(node)
    }

    if (canDropInParentFolder) {
      setParentFolderDroppableRef(node)
    }
  }

  return (
    <PlanMenuDraggableRow
      ref={setNodeRef}
      {...(canDrag ? listeners : {})}
      $canDrag={canDrag}
      transform={CSS.Translate.toString(transform)}
      transition={transition}
      isDragging={isDragging}
      isDroppable={isFolder}
      isOver={isOver || isFolderGroupOver}
      canDrop={canDropInFolder}
      data-selected={isSelected}
    >
      {row.getVisibleCells().map((cell) => (
        <BodyTd
          key={cell.id}
          {...getCellDragProps({
            attributes,
            canDrag,
            cell,
            setActivatorNodeRef
          })}
          style={{
            width: cell.column.columnDef.meta?.isFlexible
              ? '100%'
              : cell.column.getSize(),
            minWidth: cell.column.columnDef.meta?.minWidth
          }}
        >
          {isMoving ? (
            <MovingCellSkeleton
              orientation={cell.column.columnDef.meta?.orientation}
            />
          ) : (
            flexRender(cell.column.columnDef.cell, cell.getContext())
          )}
        </BodyTd>
      ))}
    </PlanMenuDraggableRow>
  )
}

export default PlanMenuTableRow
